import {use, useCallback, useMemo, useRef} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import type {IssueCommentComposerHandle} from '../comments/IssueCommentComposer'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {useLayoutAuthorLogin} from '../../stores/layout-selectors'
import {usePullRequestBasePath} from '../../stores/selectors'
import styles from './CommentsSection.module.css'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useViewModeFromSearchParams} from './data/use-comments-routing'

function ErrorFallback() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Failed to load comments</Blankslate.Heading>
      <Blankslate.Description>
        Please{' '}
        <Link inline href="#" onClick={() => window.location.reload()}>
          refresh the page
        </Link>{' '}
        to try again.
      </Blankslate.Description>
    </Blankslate>
  )
}

export function CommentsSection() {
  const [viewMode, setViewMode] = useViewModeFromSearchParams()

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section className={styles.container}>
      <ErrorBoundary
        fallback={
          <>
            <h2 className={styles.heading}>Comments</h2>
            <ErrorFallback />
          </>
        }
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent() {
  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()

  const viewMode = use(CommentsContext).viewMode
  const {filterState, setCurrentFilterState} = useCommentsFilterState(viewMode)
  const {authorList, filteredConversationItems, totalConversationItemsCount} = useFilteredCommentsData({filterState})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => composerRef.current?.insertQuote(quotedText), [])

  return (
    <>
      <div className={styles.controlsRow}>
        <h2 className={styles.heading}>Comments</h2>
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
        />
      </div>
      <Comments
        filteredConversationItems={filteredConversationItems}
        filterState={filterState}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
        totalConversationItemsCount={totalConversationItemsCount}
        composerRef={composerRef}
      />
    </>
  )
}
